import React, { useState, useMemo, useRef, useEffect } from 'react'
import type { ResearchPlan, ResearchDepth } from '@/types/intelligence'
import { ResearchDepthSelector } from './ResearchDepthSelector'
import { fetchPersonas, PersonaGroup } from '../../lib/intelligenceApi'

// Stage types for the flow
type FlowStage = 'input' | 'loading' | 'plan' | 'confirming'

const PLAN_WAIT_CANCELLED_MESSAGE = 'Research plan cancelled.'

interface InlineResearchFlowProps {
  onSubmit: (query: string, isPublic: boolean, depth: ResearchDepth) => Promise<{
    reportId: string
    plan: ResearchPlan
    planPreview: string
  }>
  onConfirm: (plan: ResearchPlan, reportId: string, query: string, isPublic: boolean, depth: ResearchDepth, updatedPersona?: string) => Promise<void>
  onCancel: (reportId?: string) => void
  disabled?: boolean
  // Optional initial values for auto-starting from landing page
  initialQuery?: string
  initialDepth?: ResearchDepth
  initialIsPublic?: boolean
  autoStart?: boolean
  onAutoStartConsumed?: () => void
}

// Progress phases for plan generation - optimized two-phase generation (~25 seconds)
const PLAN_PHASES = [
  { label: 'Classifying research domain', duration: 3000 },
  { label: 'Determining optimal analyst persona', duration: 3000 },
  { label: 'Generating research mission', duration: 4000 },
  { label: 'Building decision questions', duration: 3000 },
  { label: 'Developing mechanism & evidence probes', duration: 4000 },
  { label: 'Creating scenario questions', duration: 3000 },
  { label: 'Assembling research plan', duration: 2000 },
]

export function InlineResearchFlow({
  onSubmit,
  onConfirm,
  onCancel,
  disabled = false,
  initialQuery,
  initialDepth,
  initialIsPublic,
  autoStart,
  onAutoStartConsumed,
}: InlineResearchFlowProps) {
  const [stage, setStage] = useState<FlowStage>('input')
  const [query, setQuery] = useState(initialQuery || '')
  const [isPublic, setIsPublic] = useState(initialIsPublic ?? false)
  // Always use maximum depth (Expert Report) - no selection needed
  const selectedDepth: ResearchDepth = 'maximum'
  const autoStartTriggered = useRef(false)
  
  // Sync query state when initialQuery prop changes (for landing page handoff)
  useEffect(() => {
    if (initialQuery && initialQuery !== query && stage === 'input') {
      setQuery(initialQuery)
    }
  }, [initialQuery]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Sync isPublic state when initialIsPublic prop changes
  useEffect(() => {
    if (initialIsPublic !== undefined && stage === 'input') {
      setIsPublic(initialIsPublic)
    }
  }, [initialIsPublic]) // eslint-disable-line react-hooks/exhaustive-deps
  const [plan, setPlan] = useState<ResearchPlan | null>(null)
  const [reportId, setReportId] = useState<string | null>(null)
  const [planPreview, setPlanPreview] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const queryInputRef = useRef<HTMLTextAreaElement>(null)
  
  // Persona selection state
  const [personaGroups, setPersonaGroups] = useState<PersonaGroup[]>([])
  const [selectedPersona, setSelectedPersona] = useState<string>('')
  const [showPersonaDropdown, setShowPersonaDropdown] = useState(false)
  
  // Load personas on mount
  useEffect(() => {
    fetchPersonas().then(setPersonaGroups).catch(console.error)
  }, [])
  
  // Sync selected persona with plan - prefer recommended_persona if available
  useEffect(() => {
    if (plan && !selectedPersona) {
      // Use LLM-recommended persona if available, otherwise fall back to inputs.persona_mode
      const recommendedId = plan.recommended_persona?.persona_id
      setSelectedPersona(recommendedId || plan.inputs.persona_mode)
    }
  }, [plan, selectedPersona])
  
  // Get current persona display info
  const currentPersonaInfo = useMemo(() => {
    if (!selectedPersona) return { label: 'Auto-detect', group: '', description: '' }
    for (const group of personaGroups) {
      const persona = group.personas.find(p => p.id === selectedPersona)
      if (persona) {
        return { label: persona.label, group: group.group_label, description: persona.description }
      }
    }
    const raw = selectedPersona.replace(/_/g, ' ')
    return {
      label: raw.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      group: '',
      description: '',
    }
  }, [selectedPersona, personaGroups])
  
  // Progress tracking for loading state
  const [currentPhase, setCurrentPhase] = useState(0)
  const [phaseProgress, setPhaseProgress] = useState(0)
  
  // Animate progress when in loading stage
  useEffect(() => {
    if (stage !== 'loading') {
      setCurrentPhase(0)
      setPhaseProgress(0)
      return
    }
    
    let phaseIdx = 0
    let elapsed = 0
    const tick = 100 // ms
    
    const interval = setInterval(() => {
      if (phaseIdx >= PLAN_PHASES.length) {
        // Stay at 95% until actual completion
        setPhaseProgress(95)
        return
      }
      
      elapsed += tick
      const phase = PLAN_PHASES[phaseIdx]
      const phaseElapsed = elapsed - PLAN_PHASES.slice(0, phaseIdx).reduce((sum, p) => sum + p.duration, 0)
      
      if (phaseElapsed >= phase.duration) {
        phaseIdx++
        setCurrentPhase(phaseIdx)
      }
      
      // Calculate overall progress (leave 5% for final step)
      const totalDuration = PLAN_PHASES.reduce((sum, p) => sum + p.duration, 0)
      const overallProgress = Math.min(95, (elapsed / totalDuration) * 95)
      setPhaseProgress(Math.round(overallProgress))
    }, tick)
    
    return () => clearInterval(interval)
  }, [stage])

  // Auto-start research if coming from landing page with pending research.
  // onAutoStartConsumed is deferred until the plan is successfully generated
  // so that if the billing check fails (showing the upgrade modal), the
  // pending research can be re-triggered after payment.
  useEffect(() => {
    if (autoStart && initialQuery && query === initialQuery && !autoStartTriggered.current) {
      autoStartTriggered.current = true
      const timer = setTimeout(() => {
        handleStartWithDepth()
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [autoStart, initialQuery, query]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for retry signal (e.g. after user upgrades via UpgradeModal)
  useEffect(() => {
    const handleRetry = () => {
      if (query && query.trim().length >= 10 && stage === 'input') {
        autoStartTriggered.current = true
        handleStartWithDepth()
      }
    }
    window.addEventListener('research:retryAutoStart', handleRetry)
    return () => window.removeEventListener('research:retryAutoStart', handleRetry)
  }, [query, stage]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stage === 'input') {
      queryInputRef.current?.focus()
    }
  }, [stage])

  useEffect(() => {
    if (stage === 'input') {
      queryInputRef.current?.focus()
    }
  }, [stage])

  // Handle starting research after depth selection
  const handleStartWithDepth = async () => {
    const trimmed = query.trim()
    if (!trimmed || trimmed.length < 10) return

    setError(null)
    setStage('loading')

    try {
      const result = await onSubmit(trimmed, isPublic, selectedDepth)
      setPlan(result.plan)
      setReportId(result.reportId)
      setPlanPreview(result.planPreview)
      setStage('plan')
      onAutoStartConsumed?.()
    } catch (err) {
      if (err instanceof Error && err.message === PLAN_WAIT_CANCELLED_MESSAGE) {
        setError(null)
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to generate plan')
      setStage('input')
    }
  }

  const handleConfirm = async () => {
    if (!plan || !reportId) return
    setStage('confirming')

    try {
      // Pass updated persona if it changed
      const updatedPersona = selectedPersona !== plan.inputs.persona_mode ? selectedPersona : undefined
      await onConfirm(plan, reportId, query, isPublic, selectedDepth, updatedPersona)
      // Reset the form
      setQuery('')
      setIsPublic(false)
      setPlan(null)
      setReportId(null)
      setPlanPreview('')
      setSelectedPersona('')
      setStage('input')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start research')
      setStage('plan')
    }
  }

  const handleCancel = () => {
    // Pass reportId to parent BEFORE clearing it, so parent can cancel on backend
    const currentReportId = reportId
    setError(null)
    setPlan(null)
    setReportId(null)
    setPlanPreview('')
    setStage('input')
    onAutoStartConsumed?.()
    onCancel(currentReportId || undefined)
  }

  const personaLabel = useMemo(() => {
    // Use selectedPersona (which may be updated by user) or fall back to plan
    const personaId = selectedPersona || plan?.inputs?.persona_mode
    if (!personaId) return 'General'
    return personaId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  }, [plan, selectedPersona])
  
  const personaChanged = useMemo(() => {
    // Changed if different from BOTH the original input and the recommendation
    if (!plan || !selectedPersona) return false
    const recommendedId = plan.recommended_persona?.persona_id || plan.inputs.persona_mode
    return selectedPersona !== recommendedId
  }, [plan, selectedPersona])
  
  const recommendationConfidence = useMemo(() => {
    return plan?.recommended_persona?.confidence || 'MEDIUM'
  }, [plan])
  
  const recommendationRationale = useMemo(() => {
    return plan?.recommended_persona?.rationale || ''
  }, [plan])

  const laneHighlights = useMemo(() => {
    if (!plan?.source_lane_strategy) return []
    return plan.source_lane_strategy.map(lane => ({
      label: lane.lane,
      detail: `${lane.minimum_sources}+ sources`,
    }))
  }, [plan])

  const trimmedQuery = query.trim()
  const minCharacters = 10
  const canStart = trimmedQuery.length >= minCharacters
  const needsMoreCharacters = !canStart

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Outer container with fixed min-height for stability */}
      <div 
        className="rounded-[32px] border border-black/5 dark:border-white/5 bg-white dark:bg-gray-900 shadow-[0_35px_120px_rgba(15,23,42,0.08)] dark:shadow-[0_35px_120px_rgba(0,0,0,0.4)] overflow-hidden"
        style={{ minHeight: stage === 'input' ? '480px' : stage === 'loading' ? '280px' : '480px' }}
      >
        {/* Header - hidden during initial composer */}
        {stage !== 'input' && (
          <div className="p-8 pb-0 md:p-10 md:pb-0 transition-all duration-500 ease-out">
            <h2 
              className="text-3xl font-semibold text-slate-900 dark:text-white transition-all duration-300"
              key={stage} // Force re-render for animation
            >
              {stage === 'loading' && 'Designing Your Plan'}
              {(stage === 'plan' || stage === 'confirming') && 'Research Plan Ready'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 transition-opacity duration-300">
              {stage === 'loading' && 'Analyzing your query and designing a structured research approach...'}
              {(stage === 'plan' || stage === 'confirming') && 'Review and customize your research plan below'}
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mx-8 md:mx-10 mt-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Compose + Depth Selection */}
{stage === 'input' && (
          <div className="p-8 pt-6 md:p-10 md:pt-8 space-y-8 animate-in fade-in duration-300">
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] text-slate-400 dark:text-slate-500 uppercase mb-2">
                  Your research query
                </p>
                <div className="rounded-[20px] border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-400/20 transition">
                  <textarea
                    ref={queryInputRef}
                    value={query}
                    onChange={e => {
                      if (e.target.value.length <= 1500) setQuery(e.target.value)
                    }}
                    placeholder="What would you like to research today?"
                    maxLength={1500}
                    rows={4}
                    disabled={disabled}
                    className="w-full bg-transparent rounded-[20px] px-6 py-5 text-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none resize-none"
                  />
                  <div className="flex items-center justify-between px-6 pb-3 text-xs text-slate-400 dark:text-slate-500">
                    <span>{query.length}/1500</span>
                    <span
                      className={`text-[11px] font-medium ${needsMoreCharacters ? 'text-slate-400' : 'text-emerald-500'}`}
                    >
                      {needsMoreCharacters ? `${minCharacters} characters min` : 'Ready'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold tracking-[0.25em] text-slate-400 dark:text-slate-500 uppercase">Visibility</p>
                <div className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60 p-1">
                  <button
                    type="button"
                    onClick={() => setIsPublic(false)}
                    className={`px-4 py-2 text-xs font-semibold rounded-full transition ${
                      !isPublic
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow'
                        : 'text-slate-500 dark:text-slate-300'
                    }`}
                  >
                    Private
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsPublic(true)}
                    className={`px-4 py-2 text-xs font-semibold rounded-full transition ${
                      isPublic
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow'
                        : 'text-slate-500 dark:text-slate-300'
                    }`}
                  >
                    Public
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isPublic
                    ? 'Runs marked public will appear on Explore and cannot be reverted.'
                    : 'Your research will be private and only visible to you.'}
                </p>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <ResearchDepthSelector
                onStartResearch={handleStartWithDepth}
                isLoading={false}
                disabled={disabled}
                canStart={canStart}
              />
            </div>
          </div>
        )}

        {/* Loading Stage */}
        {stage === 'loading' && (
          <div className="p-8 pt-6 md:p-10 md:pt-8 animate-in fade-in duration-300">
            <div className="rounded-[20px] border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-6">
              <p className="text-slate-700 dark:text-slate-200 font-medium mb-4 line-clamp-2">{query}</p>
              
              {/* Current phase indicator */}
              <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mb-4">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500" />
                </span>
                <span className="transition-opacity duration-300">
                  {currentPhase < PLAN_PHASES.length ? PLAN_PHASES[currentPhase].label : 'Finalizing plan'}...
                </span>
              </div>
              
              {/* Real progress bar */}
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-300 ease-out"
                  style={{ width: `${phaseProgress}%` }}
                />
              </div>
              
              {/* Progress percentage */}
              <div className="mt-2 flex justify-between text-xs text-slate-400 dark:text-slate-500">
                <span>Phase {Math.min(currentPhase + 1, PLAN_PHASES.length)} of {PLAN_PHASES.length}</span>
                <span>{phaseProgress}%</span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="mt-4 w-full rounded-[20px] border border-slate-200 dark:border-slate-700 px-6 py-3 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Plan Review Stage - Matching Mobile Experience */}
        {(stage === 'plan' || stage === 'confirming') && plan && (
          <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-2 duration-400">
            <div className="p-8 pt-6 md:p-10 md:pt-8 space-y-6">
              
              {/* Persona Selector - Always visible at top */}
              <div className="rounded-[20px] border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Persona</p>
                    <button
                      type="button"
                      onClick={() => setShowPersonaDropdown(!showPersonaDropdown)}
                      className="flex items-center gap-2 group"
                    >
                      <span className="text-lg font-semibold text-slate-900 dark:text-white">{currentPersonaInfo.label}</span>
                      {!personaChanged && recommendationConfidence && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          recommendationConfidence === 'HIGH' 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                            : recommendationConfidence === 'MEDIUM'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {recommendationConfidence === 'HIGH' ? 'AI recommended' : 'suggested'}
                        </span>
                      )}
                      {personaChanged && <span className="text-emerald-500 text-xs font-medium">(changed)</span>}
                      <svg className={`w-4 h-4 text-slate-400 transition-transform ${showPersonaDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {recommendationRationale && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{recommendationRationale}</p>
                    )}
                  </div>
                </div>
                
                {/* Persona Dropdown */}
                {showPersonaDropdown && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="max-h-64 overflow-y-auto space-y-3">
                      {personaGroups.map(group => (
                        <div key={group.group_id}>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{group.group_label}</p>
                          <div className="space-y-1">
                            {group.personas.map(persona => (
                              <button
                                key={persona.id}
                                type="button"
                                onClick={() => {
                                  setSelectedPersona(persona.id)
                                  setShowPersonaDropdown(false)
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg transition ${
                                  selectedPersona === persona.id 
                                    ? 'bg-slate-200 dark:bg-slate-700' 
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-slate-900 dark:text-white">{persona.label}</span>
                                  {selectedPersona === persona.id && (
                                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                {persona.description && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{persona.description}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mission Overview - Always visible */}
              <div className="rounded-[20px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Mission Overview</h3>
                </div>
                <textarea
                  value={plan.plan_summary}
                  onChange={e => setPlan({ ...plan, plan_summary: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-4 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                  rows={3}
                  placeholder="Describe the research mission..."
                />
              </div>

              {/* Research Questions - Always visible */}
              {plan.research_questions && (
                <div className="space-y-4">
                  {/* Decision Questions */}
                  <div className="rounded-[16px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">Decision Questions</h4>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{plan.research_questions.decision_questions?.length || 0} items</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newQuestions = [...(plan.research_questions.decision_questions || []), '']
                            setPlan({
                              ...plan,
                              research_questions: { ...plan.research_questions, decision_questions: newQuestions }
                            })
                          }}
                          className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(plan.research_questions.decision_questions || []).map((q, idx) => (
                        <div key={`decision-${idx}`} className="group relative">
                          <textarea
                            value={q}
                            onChange={e => {
                              const newQuestions = [...plan.research_questions.decision_questions]
                              newQuestions[idx] = e.target.value
                              setPlan({
                                ...plan,
                                research_questions: { ...plan.research_questions, decision_questions: newQuestions }
                              })
                            }}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            rows={2}
                            placeholder="What decision needs to be made?"
                          />
                          {(plan.research_questions.decision_questions?.length || 0) > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newQuestions = plan.research_questions.decision_questions.filter((_, i) => i !== idx)
                                setPlan({
                                  ...plan,
                                  research_questions: { ...plan.research_questions, decision_questions: newQuestions }
                                })
                              }}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove question"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Mechanism Questions */}
                  <div className="rounded-[16px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">Mechanism Questions</h4>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{plan.research_questions.mechanism_questions?.length || 0} items</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newQuestions = [...(plan.research_questions.mechanism_questions || []), '']
                            setPlan({
                              ...plan,
                              research_questions: { ...plan.research_questions, mechanism_questions: newQuestions }
                            })
                          }}
                          className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(plan.research_questions.mechanism_questions || []).map((q, idx) => (
                        <div key={`mechanism-${idx}`} className="group relative">
                          <textarea
                            value={q}
                            onChange={e => {
                              const newQuestions = [...plan.research_questions.mechanism_questions]
                              newQuestions[idx] = e.target.value
                              setPlan({
                                ...plan,
                                research_questions: { ...plan.research_questions, mechanism_questions: newQuestions }
                              })
                            }}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            rows={2}
                            placeholder="How does this work?"
                          />
                          {(plan.research_questions.mechanism_questions?.length || 0) > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newQuestions = plan.research_questions.mechanism_questions.filter((_, i) => i !== idx)
                                setPlan({
                                  ...plan,
                                  research_questions: { ...plan.research_questions, mechanism_questions: newQuestions }
                                })
                              }}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove question"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Data Questions */}
                  <div className="rounded-[16px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">Data & Evidence Questions</h4>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{plan.research_questions.data_questions?.length || 0} items</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newQuestions = [...(plan.research_questions.data_questions || []), '']
                            setPlan({
                              ...plan,
                              research_questions: { ...plan.research_questions, data_questions: newQuestions }
                            })
                          }}
                          className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(plan.research_questions.data_questions || []).map((q, idx) => (
                        <div key={`data-${idx}`} className="group relative">
                          <textarea
                            value={q}
                            onChange={e => {
                              const newQuestions = [...plan.research_questions.data_questions]
                              newQuestions[idx] = e.target.value
                              setPlan({
                                ...plan,
                                research_questions: { ...plan.research_questions, data_questions: newQuestions }
                              })
                            }}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            rows={2}
                            placeholder="What data supports this?"
                          />
                          {(plan.research_questions.data_questions?.length || 0) > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newQuestions = plan.research_questions.data_questions.filter((_, i) => i !== idx)
                                setPlan({
                                  ...plan,
                                  research_questions: { ...plan.research_questions, data_questions: newQuestions }
                                })
                              }}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove question"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Scenario Questions */}
                  <div className="rounded-[16px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">Scenario Questions</h4>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{plan.research_questions.scenario_questions?.length || 0} items</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newQuestions = [...(plan.research_questions.scenario_questions || []), '']
                            setPlan({
                              ...plan,
                              research_questions: { ...plan.research_questions, scenario_questions: newQuestions }
                            })
                          }}
                          className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(plan.research_questions.scenario_questions || []).map((q, idx) => (
                        <div key={`scenario-${idx}`} className="group relative">
                          <textarea
                            value={q}
                            onChange={e => {
                              const newQuestions = [...plan.research_questions.scenario_questions]
                              newQuestions[idx] = e.target.value
                              setPlan({
                                ...plan,
                                research_questions: { ...plan.research_questions, scenario_questions: newQuestions }
                              })
                            }}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            rows={2}
                            placeholder="What could happen?"
                          />
                          {(plan.research_questions.scenario_questions?.length || 0) > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newQuestions = plan.research_questions.scenario_questions.filter((_, i) => i !== idx)
                                setPlan({
                                  ...plan,
                                  research_questions: { ...plan.research_questions, scenario_questions: newQuestions }
                                })
                              }}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove question"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Evidence Summary - Compact footer */}
              <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Evidence anchors required
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {laneHighlights.length} source lanes
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Persona guardrails applied
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions for Plan Stage */}
        {(stage === 'plan' || stage === 'confirming') && (
          <div className="p-8 pt-4 md:p-10 md:pt-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2.5 rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={stage === 'confirming'}
              className="px-6 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {stage === 'confirming' ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Starting...
                </>
              ) : (
                <>Approve &amp; Start Research</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InlineResearchFlow

